<template>
  <template v-if="!loading">
    <vl-grid v-vl-spacer:bottom.small class="section">
      <vl-column width="8" width-xs="12">
        <vl-title tag-name="h3"> {{ $t('entitylink.detail.title') }} </vl-title>
      </vl-column>
      <vl-column v-if="editStore.canEdit" width="4" width-xs="12">
        <vl-action-group mod-align-right>
          <vl-button
            v-if="canAddEntityLink"
            icon="plus"
            :mod-disabled="isAddDisabled"
            mod-icon-before
            @click="editStore.openCreate(DeliveryProcessDefinitionEditType.DeliveryEntityLink)"
            >{{ $t('general.add') }}</vl-button
          >
        </vl-action-group>
      </vl-column>
    </vl-grid>
    <vl-grid :class="{ edit: editStore.isCreate(DeliveryProcessDefinitionEditType.DeliveryEntityLink) }">
      <vl-column>
        <DeliveryEntityLinkCreate
          v-if="editStore.isCreate(DeliveryProcessDefinitionEditType.DeliveryEntityLink)"
          :specification-id="id"
        />
      </vl-column>
    </vl-grid>
    <vl-grid v-vl-spacer:bottom.medium class="section">
      <vl-column width="12" width-xs="12">
        <vl-accordion-list v-if="items && items.length">
          <vl-accordion-list-item v-for="(link, i) in items" :id="i" :key="i">
            <vl-accordion :id="i.toString()" :title="getLabel(link)">
              <vl-grid
                :class="{
                  edit: editStore.isEdit(DeliveryProcessDefinitionEditType.DeliveryEntityLink, link.id)
                }"
              >
                <vl-column width="8">
                  <DeliveryEntityLinkEdit
                    v-if="editStore.isEdit(DeliveryProcessDefinitionEditType.DeliveryEntityLink, link.id)"
                    ref="editForm"
                    :specification-id="id"
                    :detail="link"
                  />
                  <DeliveryEntityLinkRead v-else :detail="link" />
                </vl-column>
                <vl-column v-if="editStore.canEdit" width="4">
                  <vl-action-group
                    v-if="
                      items &&
                      items.length &&
                      !editStore.isEdit(DeliveryProcessDefinitionEditType.DeliveryEntityLink, link.id)
                    "
                    mod-align-right
                    mod-space
                    between
                  >
                    <vl-button
                      icon="pencil"
                      mod-icon-before
                      mod-secondary
                      @click="editStore.openEdit(DeliveryProcessDefinitionEditType.DeliveryEntityLink, link.id)"
                      >{{ $t('general.edit') }}</vl-button
                    >
                    <vl-button icon="trash" mod-icon-before mod-error @click="showDeleteModal(link.id)">{{
                      $t('general.delete')
                    }}</vl-button>
                  </vl-action-group>
                  <vl-action-group v-else mod-align-right mod-space between>
                    <vl-button icon="save" x mod-icon-before @click="save">{{ $t('general.save') }}</vl-button>
                    <vl-button icon="cross" mod-icon-before mod-secondary mod-error @click="editStore.closeEdit">
                      {{ $t('general.cancel') }}
                    </vl-button>
                  </vl-action-group>
                </vl-column>
                <template v-if="!editStore.isEdit(DeliveryProcessDefinitionEditType.DeliveryEntityLink, link.id)">
                  <vl-column width="10">
                    <vl-title tag-name="h4"> {{ $t('entitylink.detail.attributes') }} </vl-title>
                  </vl-column>
                  <DeliveryEntityLinkAttributes :detail="link" :specification-id="id" />
                </template>
              </vl-grid>
            </vl-accordion>
          </vl-accordion-list-item>
        </vl-accordion-list>
        <template v-else>
          <vl-annotation>{{ $t('entitylink.detail.empty') }}</vl-annotation>
        </template>
      </vl-column>
    </vl-grid>
  </template>
</template>
<script lang="ts" setup>
import { DeliveryEntityLinkDetailDto } from '@/api/backoffice/clients';
import { useDeliveryEntityLinkStore } from '@/modules/configuration/store/delivery-entity-link/delivery-entity-link.store';
import { computed, onMounted, ref, Ref } from 'vue';
import DeliveryEntityLinkRead from './DeliveryEntityLinkRead.vue';
import DeliveryEntityLinkAttributes from './DeliveryEntityLinkAttributes.vue';
import {
  DeliveryProcessDefinitionEditType,
  useDeliveryProcessDefinitionEditStore
} from '@/modules/configuration/store/delivery-process-definition/delivery-process-definition-edit.store';
import { useDeliveryEntityStore } from '@/modules/configuration/store/delivery-entity/delivery-entity.store';
import DeliveryEntityLinkCreate from './DeliveryEntityLinkCreate.vue';
import DeliveryEntityLinkEdit from './DeliveryEntityLinkEdit.vue';
import { useModalStore } from '@/modules/core/store/modal.store';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const entityLinkStore = useDeliveryEntityLinkStore();
const entityStore = useDeliveryEntityStore();
const editStore = useDeliveryProcessDefinitionEditStore();
const modalStore = useModalStore();
const props = defineProps({
  id: {
    type: Number,
    required: true
  }
});

const editForm: Ref<typeof DeliveryEntityLinkEdit | (typeof DeliveryEntityLinkEdit)[]> = ref(null);

const loading = computed((): boolean => {
  return entityLinkStore.loading;
});

const items = computed((): DeliveryEntityLinkDetailDto[] => {
  return entityLinkStore.bySpecification(props.id);
});

const isAddDisabled = computed(() => {
  return (
    editStore.isEdit(DeliveryProcessDefinitionEditType.DeliveryEntityLink) ||
    editStore.isCreate(DeliveryProcessDefinitionEditType.DeliveryEntity)
  );
});

const canAddEntityLink = computed(() => {
  return entityStore.bySpecification(props.id).length >= 2;
});

onMounted(async () => {
  if (props.id) {
    await entityLinkStore.getBySpecification(props.id);
  }
});

const save = () => {
  if (Array.isArray(editForm?.value)) {
    editForm.value[0].onSubmit();
  } else {
    editForm.value.onSubmit();
  }
};

const showDeleteModal = (entityId: number) => {
  modalStore.showDeleteModal({
    title: t('entitylink.delete.title'),
    props: {
      confirm: () => entityLinkStore.deleteEntity(props.id, entityId)
    }
  });
};

const getLabel = (item: DeliveryEntityLinkDetailDto): string => {
  const unknown = '(?)';
  const from = `${item.fromEntity?.code ?? unknown}`;
  const to = `${item.toEntity?.code ?? unknown}`;
  return t('entitylink.detail.linkTo', { from, to });
};
</script>
<style lang="scss" scoped>
:deep(.vl-accordion__panel) {
  padding-right: 0;
}
.vl-grid {
  flex-grow: 1;
}
.section {
  padding-left: 20px;
}
</style>
